import React, { useState } from 'react';
import { Plus, Eye, Edit2, Trash2, Shield, Key, Users, ChevronDown, ChevronUp } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import RoleForm from './RoleForm';

const RoleList = () => {
  const [view, setView] = useState('list');
  const [selectedRole, setSelectedRole] = useState(null);
  
  const [roles, setRoles] = useState([
    {
      id: 1,
      name: 'Admin',
      description: 'Full system access with all permissions',
      permissions: [
        'user.create', 'user.read', 'user.update', 'user.delete',
        'role.create', 'role.read', 'role.update', 'role.delete',
        'permission.create', 'permission.read', 'permission.update', 'permission.delete',
        'project.create', 'project.read', 'project.update', 'project.delete',
        'budget.create', 'budget.read', 'budget.update', 'budget.delete',
        'report.generate', 'report.export'
      ],
      userCount: 1,
      isSystem: true
    },
    {
      id: 2,
      name: 'Project Manager',
      description: 'Manage projects, view reports, and oversee team activities',
      permissions: [
        'project.create', 'project.read', 'project.update',
        'budget.read', 'budget.update',
        'report.generate', 'report.export',
        'user.read'
      ],
      userCount: 3,
      isSystem: false
    },
    {
      id: 3,
      name: 'Finance Manager',
      description: 'Manage budgets, financials, and generate financial reports',
      permissions: [
        'budget.create', 'budget.read', 'budget.update', 'budget.delete',
        'project.read',
        'report.generate', 'report.export',
        'user.read'
      ],
      userCount: 2,
      isSystem: false
    },
    {
      id: 4,
      name: 'Site Engineer',
      description: 'View and update project progress in the field',
      permissions: [
        'project.read', 'project.update',
        'report.generate'
      ],
      userCount: 5,
      isSystem: false
    },
    {
      id: 5,
      name: 'QC Inspector',
      description: 'Quality control inspections and reporting',
      permissions: [
        'project.read',
        'report.generate', 'report.export'
      ],
      userCount: 3,
      isSystem: false
    },
    {
      id: 6,
      name: 'Procurement',
      description: 'Manage procurement and vendor relationships',
      permissions: [
        'project.read',
        'budget.read',
        'report.generate'
      ],
      userCount: 2,
      isSystem: false
    },
    {
      id: 7,
      name: 'Viewer',
      description: 'Read-only access to view projects and reports',
      permissions: [
        'project.read',
        'budget.read',
        'report.generate'
      ],
      userCount: 4,
      isSystem: false
    }
  ]);

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Filter roles based on search
  const filteredRoles = roles.filter(role => {
    const matchesSearch = 
      role.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      role.description.toLowerCase().includes(searchValue.toLowerCase());
    return matchesSearch;
  });

  // Sort roles
  const sortedRoles = [...filteredRoles].sort((a, b) => {
    const aValue = a[sortConfig.sortBy];
    const bValue = b[sortConfig.sortBy];
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate roles
  const startIndex = currentPage * pageSize;
  const paginatedRoles = sortedRoles.slice(startIndex, startIndex + pageSize);

  // Get permission categories
  const getPermissionsByCategory = (permissions) => {
    const categories = {};
    permissions.forEach(perm => {
      const [resource, action] = perm.split('.');
      if (!categories[resource]) {
        categories[resource] = [];
      }
      categories[resource].push(action);
    });
    return categories;
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'name',
      label: 'Role Name',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center shadow-md">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{row.name}</div>
            {row.isSystem && (
              <span className="text-xs text-blue-600 font-medium">System Role</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <span className="text-gray-700">{row.description}</span>
      )
    },
    {
      key: 'permissions',
      label: 'Permissions',
      sortable: false,
      nowrap: false,
      render: (row) => {
        const categories = getPermissionsByCategory(row.permissions);
        const categoryCount = Object.keys(categories).length;
        
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-4 h-4 text-purple-600" />
              <span className="font-semibold text-sm text-gray-900">
                {row.permissions.length} permissions
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.keys(categories).slice(0, 3).map(category => (
                <span key={category} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  {category} ({categories[category].length})
                </span>
              ))}
              {categoryCount > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  +{categoryCount - 3} more
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'userCount',
      label: 'Users',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-900">{row.userCount}</span>
          <span className="text-xs text-gray-500">users</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button 
            onClick={() => handleView(row)} 
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button 
            onClick={() => handleEdit(row)} 
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
            disabled={row.isSystem}
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button 
            onClick={() => handleDelete(row)} 
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
            disabled={row.isSystem}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleAddNew = () => {
    setView('add');
    setSelectedRole(null);
  };

  const handleView = (role) => {
    setSelectedRole(role);
    setView('detail');
  };

  const handleEdit = (role) => {
    if (role.isSystem) {
      alert('Cannot edit system role!');
      return;
    }
    setSelectedRole(role);
    setView('edit');
  };

  const handleDelete = (role) => {
    if (role.isSystem) {
      alert('Cannot delete system role!');
      return;
    }
    if (role.userCount > 0) {
      alert(`Cannot delete role "${role.name}". There are ${role.userCount} users assigned to this role.`);
      return;
    }
    if (window.confirm(`Are you sure you want to delete role "${role.name}"?`)) {
      setRoles(prev => prev.filter(r => r.id !== role.id));
      alert('Role deleted successfully!');
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedRole(null);
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Calculate stats
  const totalPermissions = roles.reduce((sum, role) => sum + role.permissions.length, 0);
  const avgPermissions = Math.round(totalPermissions / roles.length);

  // Render Form View
  if (view === 'add' || view === 'edit') {
    return <RoleForm onBack={handleBackToList} roleData={selectedRole} mode={view} />;
  }

  // Render Detail View
  if (view === 'detail' && selectedRole) {
    return <RoleDetail role={selectedRole} onBack={handleBackToList} onEdit={() => handleEdit(selectedRole)} />;
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Data Role</h1>
            <p className="text-gray-600 mt-1">Halaman untuk mengelola Role</p>
          </div>
          <button 
            onClick={handleAddNew} 
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Role
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Total Roles</p>
              <p className="text-3xl font-bold mt-2">{roles.length}</p>
              <p className="text-purple-100 text-sm mt-2">Available roles</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Permissions</p>
              <p className="text-3xl font-bold mt-2">{totalPermissions}</p>
              <p className="text-blue-100 text-sm mt-2">Across all roles</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Avg Permissions</p>
              <p className="text-3xl font-bold mt-2">{avgPermissions}</p>
              <p className="text-green-100 text-sm mt-2">Per role</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        data={paginatedRoles}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredRoles.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={false}
        onSearch={handleSearch}
        searchValue={searchValue}
        searchPlaceholder="Search by role name or description..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

// Role Detail Component with Accordion
const RoleDetail = ({ role, onBack, onEdit }) => {
  const [expandedPanel, setExpandedPanel] = useState('basic');

  const togglePanel = (panel) => {
    setExpandedPanel(expandedPanel === panel ? null : panel);
  };

  const getPermissionsByCategory = (permissions) => {
    const categories = {};
    permissions.forEach(perm => {
      const [resource, action] = perm.split('.');
      if (!categories[resource]) {
        categories[resource] = [];
      }
      categories[resource].push(action);
    });
    return categories;
  };

  const Panel = ({ title, name, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => togglePanel(name)}
        className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
          expandedPanel === name 
            ? 'bg-gradient-to-r from-blue-50 to-red-50' 
            : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <h3 className={`text-lg font-bold ${expandedPanel === name ? 'text-red-600' : 'text-gray-800'}`}>
          {title}
        </h3>
        {expandedPanel === name ? (
          <ChevronUp className="w-5 h-5 text-red-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expandedPanel === name && (
        <div className="p-6 bg-white">
          {children}
        </div>
      )}
    </div>
  );

  const permissionCategories = getPermissionsByCategory(role.permissions);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Role Details</h1>
              <p className="text-gray-600 mt-1">{role.name}</p>
            </div>
          </div>
          <button 
            onClick={onEdit} 
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            disabled={role.isSystem}
          >
            <Edit2 className="w-5 h-5" />
            Edit Role
          </button>
        </div>
      </div>

      {/* Accordion Panels */}
      <div className="space-y-4">
        <Panel title="Basic Information" name="basic">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Role Name</label>
              <p className="text-lg font-semibold text-gray-900">{role.name}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Users Assigned</label>
              <p className="text-lg font-bold text-gray-900">{role.userCount} users</p>
            </div>
            <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-semibold text-gray-500 mb-2">Description</label>
              <p className="text-base text-gray-900">{role.description}</p>
            </div>
          </div>
        </Panel>

        <Panel title={`Permissions (${role.permissions.length})`} name="permissions">
          <div className="space-y-4">
            {Object.entries(permissionCategories).map(([resource, actions]) => (
              <div key={resource} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Key className="w-5 h-5 text-purple-600" />
                  <h4 className="text-lg font-bold text-gray-900 capitalize">{resource}</h4>
                  <span className="text-sm text-gray-500">({actions.length} permissions)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {actions.map(action => (
                    <span key={action} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      {action}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default RoleList;